import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { WaardelijstenApi } from "../api/waardelijsten";
import { DropdownInfoFactory, WaardelijstFactory } from "../factories";
import { OrgMembershipsFactory } from "../factories/orgMemberships";
import { IdentifierWithChildren } from "../factories/waardelijst";
import { Identifier } from "../models";
import { Bestand } from "../models/document-metadata";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import { FormState } from "../store/reducers/document-metadata";
import { selectMetadata } from "../store/selectors";
import { formatDate, timeFormatter } from "./DateFormatter";

export const UseFormContextDocuments = () => {
  const factories = useMemo(
    () => ({
      waardelijsten: new WaardelijstenApi(),
      waardelijstFactory: new WaardelijstFactory(),
      orgMemberships: new OrgMembershipsFactory(),
      dropdownInfo: new DropdownInfoFactory(),
    }),
    []
  );

  const dispatch = useAppDispatch();
  const root = useAppSelector(selectMetadata);
  const { data: formStates, formIndex, documentError, localValidationError, setIndex } = useAppSelector((s) => s.meta);
  const { mode } = useAppSelector((s) => s.meta);
  const formState = formStates[setIndex]?.[formIndex];
  const authState = useAppSelector((s) => s.auth);

  const { id } = useParams();

  const hasDocumentErrors = () => documentError.indexList && documentError.indexList.length > 0;

  const hasErrorReviewed = () => root.errorReviewed && !(hasDocumentErrors() && id && documentError.pid?.includes(id));

  return {
    factories,
    dispatch,
    root,
    formStates,
    formState,
    formStateSetIndex: setIndex,
    formStateIndex: formIndex,
    documentError,
    localValidationError,
    mode,
    authState,
    id,
    hasDocumentErrors,
    hasErrorReviewed,
  };
};

export const UseFormContextStatesDocument = () => {
  const [uitgesteldPubliceren, setUitgesteldPubliceren] = useState(false);
  const [documentDatums, setDocumentDatums] = useState<string[]>([formatDate(new Date())]);
  const [popupUpdate, setPopupUpdate] = useState(false);
  const [popupHerindexeren, setPopupHerindexeren] = useState(false);
  const [redenUpdate, setRedenUpdate] = useState<Identifier>();
  const [redenIntrekkingVervanging, setRedenIntrekkingVervanging] = useState<IdentifierWithChildren[]>([]);
  const [ronlOnderwerpen, setRonlOnderwerpen] = useState<IdentifierWithChildren[]>([]);
  const [actievePublishers, setActievePublishers] = useState<IdentifierWithChildren[]>([]);
  const [taal, setTaal] = useState<IdentifierWithChildren[]>([]);
  const [informatiecategorieen, setInformatiecategorieen] = useState<IdentifierWithChildren[]>([]);
  const [currentPublishDate, setCurrentPublishDate] = useState(new Date());
  const [currentPublishTime, setCurrentPublishTime] = useState<string>(timeFormatter(new Date()));
  const [concept, setConcept] = useState(false);
  const [weblocatieVoorWijziging, setWeblocatieVoorWijziging] = useState<string>();
  const [hasChangedMetaVoorWijziging, setHasChangedMetaVoorWijziging] = useState(false);
  const [bestandenVoorWijziging, setBestandenVoorWijziging] = useState<Bestand[]>();

  const snapshotVoorWijziging = (form: FormState) => {
    setWeblocatieVoorWijziging(form.meta.document.weblocatie);
    setHasChangedMetaVoorWijziging(form.hasChangedMeta ?? false);
    setBestandenVoorWijziging(form.meta.versies?.at(0)?.bestanden);
  };

  return {
    uitgesteldPubliceren,
    setUitgesteldPubliceren,
    documentDatums,
    setDocumentDatums,
    popupUpdate,
    setPopupUpdate,
    popupHerindexeren,
    setPopupHerindexeren,
    redenUpdate,
    setRedenUpdate,
    redenIntrekkingVervanging,
    setRedenIntrekkingVervanging,
    ronlOnderwerpen,
    setRonlOnderwerpen,
    actievePublishers,
    setActievePublishers,
    taal,
    setTaal,
    informatiecategorieen,
    setInformatiecategorieen,
    currentPublishDate,
    setCurrentPublishDate,
    currentPublishTime,
    setCurrentPublishTime,
    concept,
    setConcept,
    weblocatieVoorWijziging,
    hasChangedMetaVoorWijziging,
    bestandenVoorWijziging,
    snapshotVoorWijziging,
  };
};
